import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject, combineLatest, interval } from 'rxjs';
import { map, switchMap, takeUntil, takeWhile } from 'rxjs/operators';
import { AppService } from 'src/app/app.service';
import { CompanyService } from '../company.service';
import {
  isLockForValidationSelector,
  isPrfLocked,
  validationSummaryJobIdSelector,
} from '../premiumreport/data-access/premium-report.selectors';
import * as ValidationSummaryActions from './data-access/validation-summary-actions';
import * as premiumReportActions from '../premiumreport/data-access/premium-report.action';
import {
  selectAllFeedbackValid,
  selectAllStatusSuccess,
  selectNumberOfCompanyValidationMessage,
  selectShowNumberOfCompanyValidationMessage,
  selectValidationSummaryData,
  selectValidationSummaryIsComplete,
} from './data-access/validation-summary-selectors';
import { selectGroupCode, selectPrfName } from 'src/app/store/selectors/app.selector';

@Component({
    selector: 'app-validation-summary',
    templateUrl: './validation-summary.component.html',
    styleUrls: ['./validation-summary.component.scss'],
    standalone: false
})
export class ValidationSummaryComponent implements OnInit, OnDestroy {
  constructor(
    private companyService: CompanyService,
    private store: Store,
    private appService: AppService,
    private router: Router
  ) {}

  compno: any;
  prfNumber: any;
  prfName: any;
  groupcode: any;
  isAdmin: boolean =false;
  validationSummaryItem$;
  selectAllFeedbackValid$;
  selectValidationSummaryIsComplete$;
  selectValidationSummaryIsReadOnly$;
  private destroy$ = new Subject<void>();
  selectIsLockForValidation$ = this.store.select(isLockForValidationSelector);
  selectIsPrfLocked$ = this.store.select(isPrfLocked);
  selectAllStatusSuccess$ = this.store.select(selectAllStatusSuccess);
  selectShowNumberOfCompanyValidationMessage$ = this.store.select(selectShowNumberOfCompanyValidationMessage);
  selectNumberOfCompanyValidationMessage$ = this.store.select(selectNumberOfCompanyValidationMessage);
  selectValidationJobId$ = this.store.select(validationSummaryJobIdSelector);
  prfYear: number;

  isReadOnly$ = combineLatest([
    this.selectIsLockForValidation$,
    this.selectIsPrfLocked$,
  ]).pipe(
    map(([isLockForValidation, isPrfLocked]) => {
      return (
        !isLockForValidation || isPrfLocked || this.appService.isReadonlyAdmin
      );
    })
  );

  ngOnInit(): void {
    this.prfYear =  this.companyService.currentYear;
    this.compno = this.companyService.companyId;
    this.prfNumber = this.companyService.prfNumber;
    this.store.select(selectPrfName).subscribe(prfName => {
      this.prfName = prfName;
    });
    if (this.appService.isAdmin) {
      this.isAdmin = true;
    }

     this.store.select(selectGroupCode).subscribe((groupCode) => {
       this.groupcode = groupCode;
       if (!this.groupcode) {
         this.companyService.getgroup().subscribe((value) => {
           this.groupcode = value;
         });
       }
     });
     this.companyService.getPrfStatus();
     this.selectValidationJobId$.subscribe((jobId) => {
       if (!jobId) {
         this.store.dispatch(
           premiumReportActions.loadPremiumInfo({
             compno: this.compno,
             currentYear: this.prfYear,
             groupcode: this.groupcode,
           }),
         );
       }
     });
   
    this.loadValidationSummaryData();
    this.validationSummaryItem$ = this.store.select(
      selectValidationSummaryData
    );
    this.selectAllFeedbackValid$ = this.store.select(selectAllFeedbackValid);
    this.selectValidationSummaryIsComplete$ = this.store.select(
      selectValidationSummaryIsComplete
    );

    interval(3000)
      .pipe(
        // Use switchMap to switch to the latest value
        switchMap(() => this.selectValidationSummaryIsComplete$),
        // Continue until isComplete is true
        takeWhile((isComplete) => !isComplete),
        // Unsubscribe when the component is destroyed
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.loadValidationSummaryData());
      this.selectValidationJobId$.subscribe(id=>{
        console.log(`id:${id}`);
        this.loadValidationSummaryData();
      });

      // Check submission source and redirect to PRF Submission Complete if conditions are met
      combineLatest({
        isComplete: this.selectValidationSummaryIsComplete$,
        isLockForValidation: this.selectIsLockForValidation$
      }).pipe(
        takeUntil(this.destroy$)
      ).subscribe(({ isComplete, isLockForValidation }) => {
        const submissionFrom = sessionStorage.getItem('submissionFrom');

        if (submissionFrom && isComplete && !isLockForValidation) {
          sessionStorage.removeItem('submissionFrom');
          this.navigateToPRFSubmissionComplete();
        }
      });
  }

  ngOnDestroy(): void {
    // Unsubscribe when the component is destroyed
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadValidationSummaryData() {
    this.store.dispatch(
      ValidationSummaryActions.loadAllValidationSummaryItems()
    );
  }

  private navigateToPRFSubmissionComplete() {
    const route = `/${this.prfYear}/${this.prfNumber}/PRFSubmissionComplete`;
    this.router.navigate([route]);
  }
}
